import { getPreferredCalendarPeriodView } from '@app/features/calendar/calendar-preferences';
import {
  CALENDAR_ROUTE_ID,
  CALENDAR_SEARCH_NAMESPACE,
  calendarPath,
} from '@app/features/calendar-view/calendar-url';
import { CALENDAR_VIEW_ID } from '@app/features/calendar-view/types';
import { channelsSearch } from '@app/features/channels-view/channels-route';
import { crmPipelineSearch } from '@app/features/crm/crm-route';
import {
  driveDocumentFromContent,
  drivePath,
} from '@app/features/drive-view/primitives/drive-route';
import { driveDocumentBlockType } from '@app/features/drive-view/primitives/drive-route-schema';
import { driveSearch } from '@app/features/drive-view/primitives/drive-search';
import { URL_PARAMS as EMAIL_URL_PARAMS } from '@app/features/email-thread/core/location';
import { EMAIL_DETAIL_SEARCH_NAMESPACE } from '@app/features/email-view/email-route';
import {
  replacePaneSearchParams,
  routeParams,
  type SerializedSearchParams,
  type SplitRouterMiddleware,
  type SplitRouterMiddlewareContext,
  type SplitRouterMiddlewareResult,
} from '@app/lib/split-router';
import { paneRootMatch } from '@app/routes/app-route';
import { URL_PARAMS as CALL_URL_PARAMS } from '@block-call/constants';
import { URL_PARAMS as CHANNEL_URL_PARAMS } from '@block-channel/constants';
import { URL_PARAMS as MD_URL_PARAMS } from '@block-md/constants';
import { URL_PARAMS as PDF_URL_PARAMS } from '@block-pdf/constants';
import { match } from 'ts-pattern';
import { decodeLegacyPair } from './legacy-route';

type AppMiddlewareState = {
  isTouchDevice: () => boolean;
};

/** Upgrade legacy component and block URLs when supported; on touch, keep
 * document details in their full-block routes instead of inline Drive views. */
function redirectLegacyRoutes(
  { routes, to, redirect }: SplitRouterMiddlewareContext,
  options: AppMiddlewareState
): SplitRouterMiddlewareResult | undefined {
  const route = to.location.route;
  const rootId = paneRootMatch(route)?.id;

  const isTouch = options.isTouchDevice();

  if (rootId === 'drive' && isTouch) {
    const { documentType, documentId } = routeParams(route);
    if (typeof documentType === 'string' && typeof documentId === 'string') {
      return redirect(
        `/${driveDocumentBlockType(documentType)}/${encodeURIComponent(documentId)}`
      );
    }
  }

  if (rootId === 'call-detail') {
    const { callId } = routeParams(route);
    if (typeof callId === 'string') {
      return redirect(`/calls/call/${encodeURIComponent(callId)}`);
    }
  }
  if (rootId === 'pr-detail') {
    const { foreignEntityId } = routeParams(route);
    if (typeof foreignEntityId === 'string') {
      return redirect(`/reviews/pr/${encodeURIComponent(foreignEntityId)}`);
    }
  }
  if (rootId !== 'legacy-content') return;

  const { type, id } = routeParams(route);

  if (typeof type !== 'string' || typeof id !== 'string') return;

  const content = decodeLegacyPair(type, id);
  if (!content) return;

  const path = match(content)
    .with({ type: 'component', id: 'documents' }, () => '/drive')
    .with({ type: 'component', id: 'settings' }, () => '/settings')
    .with({ type: 'component', id: CALENDAR_VIEW_ID }, () =>
      calendarPath(getPreferredCalendarPeriodView())
    )
    .with({ type: 'component' }, ({ id }) =>
      routes.byId.has(`view-${id}`) ? `/${id}` : undefined
    )
    // A pipeline has no view of its own; Customers opens on it, on touch too.
    .with({ type: 'pipeline' }, ({ id }) => {
      const query = new URLSearchParams();
      replacePaneSearchParams(query, [crmPipelineSearch(id)]);
      return `/companies?${query}`;
    })
    .when(
      () => isTouch,
      () => undefined
    )
    .with({ type: 'email' }, ({ id }) => `/mail/${encodeURIComponent(id)}`)
    .with(
      { type: 'channel' },
      ({ id }) => `/channels/${encodeURIComponent(id)}`
    )
    .with({ type: 'task' }, ({ id }) => `/tasks/${encodeURIComponent(id)}`)
    // Like a task, a project opens in Tasks, under its Projects tab.
    .with({ type: 'initiative' }, ({ id }) => {
      const query = new URLSearchParams();
      replacePaneSearchParams(query, [{ tasks: { tab: ['projects'] } }]);
      return `/tasks/projects/${encodeURIComponent(id)}/overview?${query}`;
    })
    .otherwise((content) => {
      const document = driveDocumentFromContent(content);
      return document
        ? drivePath({ kind: 'tab', tab: 'owned' }, document)
        : undefined;
    });

  if (!path) return;

  return redirect(path);
}

/** On external entry, copy legacy detail query keys into the destination pane.
 * Keep repeated values and let explicit pane-local values take precedence. */
function migrateLegacySearch({
  to,
  path,
  cause,
  externalSearch,
  redirect,
}: SplitRouterMiddlewareContext): SplitRouterMiddlewareResult | undefined {
  if (cause !== 'initial' && cause !== 'external') return;

  if (!externalSearch) return;

  const leafId = to.location.route.matches.at(-1)?.id;
  const homeChannel =
    leafId === 'home-channel' ||
    (leafId === 'home-preview' &&
      routeParams(to.location.route).blockType === 'channel');
  const homeDocumentType =
    leafId === 'home-document'
      ? routeParams(to.location.route).documentType
      : leafId === 'home-preview'
        ? routeParams(to.location.route).blockType
        : undefined;
  const commentKey = (() => {
    switch (homeDocumentType) {
      case 'md':
      case 'task':
      case 'skill':
      case 'snippet':
      case 'spreadsheet':
        return MD_URL_PARAMS.commentId;
      case 'pdf':
        return PDF_URL_PARAMS.annotationId;
    }
  })();
  const homeDocumentMapping = commentKey
    ? {
        namespace: driveSearch.namespace,
        fields: [[commentKey, 'commentId']] as const,
      }
    : undefined;

  const mapping = match(leafId)
    .with('mail-thread', () => ({
      namespace: EMAIL_DETAIL_SEARCH_NAMESPACE,
      fields: [[EMAIL_URL_PARAMS.messageId, 'messageId']] as const,
    }))
    .when(
      (id) => id === 'channels-channel' || homeChannel,
      () => ({
        namespace: channelsSearch.namespace,
        fields: [
          [CHANNEL_URL_PARAMS.message, 'messageId'],
          [CHANNEL_URL_PARAMS.thread, 'threadId'],
        ] as const,
      })
    )
    .when(
      (id) => id === 'home-document' || id === 'home-preview',
      () => homeDocumentMapping
    )
    .with(CALENDAR_ROUTE_ID, () => ({
      namespace: CALENDAR_SEARCH_NAMESPACE,
      fields: [['eventId', 'eventId']] as const,
    }))
    .with('call-detail', 'drive-call', 'calls-call', () => ({
      namespace: 'call-detail',
      fields: [
        [CALL_URL_PARAMS.transcriptId, 'transcriptId'],
        [CALL_URL_PARAMS.messageId, 'messageId'],
      ] as const,
    }))
    .otherwise(() => undefined);

  if (!mapping) return;

  const { namespace, fields } = mapping;

  const raw = new URLSearchParams(externalSearch);
  const current = to.location.search?.[namespace] ?? {};
  const additions: SerializedSearchParams = {};

  for (const [legacyKey, field] of fields) {
    // Explicit canonical values, including empty ones, take precedence.
    if (Object.hasOwn(current, field)) continue;

    const values = raw.getAll(legacyKey);

    if (values.length) additions[field] = values;
  }

  if (!Object.keys(additions).length) return;

  const search = {
    ...to.location.search,
    [namespace]: { ...current, ...additions },
  };

  const query = new URLSearchParams();

  // Middleware redirects describe one entry; the router assigns its pane index.
  replacePaneSearchParams(query, [search]);

  return redirect(`${path}?${query}`);
}

export function createAppSplitRouterMiddleware(
  state: AppMiddlewareState
): readonly SplitRouterMiddleware[] {
  return [
    (context) => redirectLegacyRoutes(context, state),
    migrateLegacySearch,
  ];
}
